'use client';

import { useRouter } from 'next/navigation';
import { useTranslations } from 'next-intl';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { IconSparkles } from '@tabler/icons-react';

export default function InsufficientCreditsModal({
  isOpen,
  onClose,
  onTopUp,
  canUpgrade = true,
}) {
  const router = useRouter();
  const tCreditPacks = useTranslations('CreditPacks');

  const handleUpgrade = () => {
    onClose?.();
    router.push('/pricing');
  };

  const handleTopUp = () => {
    onTopUp?.();
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent
        overlayClassName="bg-black/55 backdrop-blur-md"
        className="w-[calc(100vw-2rem)] max-w-[560px] overflow-hidden border border-[#3a2c22] bg-[linear-gradient(180deg,rgba(33,25,21,0.96)_0%,rgba(20,16,14,0.98)_100%)] p-0 text-white shadow-[0_28px_100px_rgba(0,0,0,0.45)] sm:rounded-[28px]"
      >
        <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-amber-400/70 to-transparent" />

        <div className="relative p-7 sm:p-8">
          <DialogHeader className="space-y-4 text-left">
            <div className="inline-flex h-12 w-12 items-center justify-center rounded-2xl border border-amber-400/20 bg-gradient-to-br from-amber-400/18 via-orange-400/12 to-transparent text-amber-300 shadow-[0_12px_30px_rgba(245,158,11,0.18)]">
              <IconSparkles size={24} />
            </div>
            <div className="space-y-2">
              <DialogTitle className="font-playfair text-[2rem] leading-none tracking-[-0.03em] text-white">
                Not Enough Credits
              </DialogTitle>
              <DialogDescription className="max-w-md text-sm leading-6 text-neutral-300">
                Your current credit balance is not enough for this request. Please upgrade your
                subscription plan or purchase permanent credits to continue.
              </DialogDescription>
            </div>
          </DialogHeader>

          <div className="mt-6 rounded-[22px] border border-white/8 bg-white/[0.03] px-4 py-3 text-sm leading-6 text-neutral-300">
            Choose one of the options below to continue your creation.
          </div>

          <div className="mt-6 flex flex-col-reverse gap-3 sm:flex-row sm:justify-end">
            <Button
              type="button"
              variant="ghost"
              onClick={onClose}
              className="rounded-xl border border-white/10 bg-white/[0.03] px-5 text-neutral-200 hover:bg-white/[0.06] hover:text-white"
            >
              Close
            </Button>
            {canUpgrade && (
              <Button
                type="button"
                onClick={handleUpgrade}
                className="rounded-xl bg-gradient-to-r from-amber-500 to-orange-500 px-5 text-white shadow-[0_16px_38px_rgba(245,158,11,0.28)] transition-transform duration-200 hover:scale-[1.02] hover:from-amber-400 hover:to-orange-400"
              >
                Upgrade
              </Button>
            )}
            <Button
              type="button"
              onClick={handleTopUp}
              className="rounded-xl bg-gradient-to-r from-sky-500 to-cyan-500 px-5 text-white shadow-[0_16px_38px_rgba(14,165,233,0.24)] transition-transform duration-200 hover:scale-[1.02] hover:from-sky-400 hover:to-cyan-400"
            >
              {tCreditPacks('title')}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
